// src/App.tsx

import { useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { AppErrorBoundary } from "@/components/errors/AppErrorBoundary";
import { NotificationProvider } from "@/context/NotificationContext";
import { AccountScopedWatermarks } from "@/context/AccountWatermarksContext";
import { DashboardDefaultsProvider } from "@/context/DashboardDefaultsContext";
import { PreferencesProvider } from "@/context/PreferencesContext";
import { Routes, Route, useParams, Outlet, useLocation, createRoutesFromElements } from "react-router-dom";
import { LegacyTenantRedirect, Navigate, TENANT_ROUTE_BASE } from "@/routing/accountRouting";
import { TenantScope } from "@/routing/TenantScope";
import { registerTenantRoutes } from "@/routing/sectionRoot";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { OnboardingProvider } from "@/context/OnboardingContext";
import { DiscoveryJobProvider } from "@/context/DiscoveryJobContext";
import { GeneratingMessagesProvider } from "@/context/GeneratingMessagesContext";
import { DwhProvisioningProvider } from "@/context/DwhProvisioningContext";
import { ReportBuildProvider } from "@/context/ReportBuildContext";
import OnboardingModal from "@/components/onboarding/OnboardingModal";
import { OnboardingDevToggle } from "@/components/onboarding/OnboardingDevToggle";
import { Center, Loader, Text, Stack } from "@mantine/core";

import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { SettingsFrame, DataManagementFrame } from "@/components/layout/sectionFrames";
import InsightsSectionLayout from "@/components/layout/InsightsSectionLayout";
import { MenuGuard } from "@/components/auth/MenuGuard";
import { FeatureFlagGuard } from "@/components/auth/FeatureFlagGuard";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import LandingRedirect from "@/components/auth/LandingRedirect";
import { AppHeader } from "@/components/layout/AppHeader";
import { AssistantProvider } from "@/context/AssistantContext";
import { SourceWizardProvider } from "@/context/SourceWizardContext";
import { AuthorizationDrawerProvider } from "@/context/AuthorizationDrawerContext";
import Login from "@/pages/Login";
import TestPage from "@/pages/TestPage";
import { MinDurationSuspense } from "@/components/loaders/PageLoader";
import { AuthBootstrapGate } from "@/components/auth/AuthBootstrapGate";
import VerityLoaderShowcase from "@/components/loaders/VerityLoaderShowcase";
import { ANALYST_READER_ROLES } from "@/components/analyst/analystAccess";

// Lazy load the chart-heavy pages. These four used to be static imports, which
// dragged their whole dependency tree — echarts above all, via
// components/charts/* — into the entry chunk, so every user parsed
// the charting stack just to reach a segments list. `Login` stays static on
// purpose: it is the first paint for logged-out users, where a lazy chunk would
// only add a round-trip.
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"));
const ChatPage = lazyWithRetry(() => import("@/pages/ChatPage"));
const Insights = lazyWithRetry(() => import("@/pages/Insights"));

// Lazy load the public (pre-shell) routes.
const Logout = lazyWithRetry(() => import("@/pages/Logout"));
const MagicLinkCallback = lazyWithRetry(() => import("@/pages/MagicLinkCallback"));
const AcceptInvite = lazyWithRetry(() => import("@/pages/AcceptInvite"));

// Lazy load setup pages
const SourceSetupPage = lazyWithRetry(() => import("@/pages/SourceSetupPage"));
const NotificationsPage = lazyWithRetry(() => import("@/pages/NotificationsPage"));
const AgentRequestPage = lazyWithRetry(() => import("@/pages/AgentRequestPage"));

// Lazy load analytics pages
const InsightsHome = lazyWithRetry(() => import("@/pages/InsightsHome"));
const AnalyticsLanding = lazyWithRetry(() => import("@/pages/AnalyticsLanding"));
const DeepDives = lazyWithRetry(() => import("@/pages/DeepDives"));
const DeepDiveNew = lazyWithRetry(() => import("@/pages/DeepDiveNew"));
const DeepDiveReport = lazyWithRetry(() => import("@/pages/DeepDiveReport"));
const AutomationsLanding = lazyWithRetry(() => import("@/pages/AutomationsLanding"));
const AutomationDetail = lazyWithRetry(() => import("@/pages/AutomationDetail"));
const AutomationCreate = lazyWithRetry(() => import("@/pages/AutomationCreate"));
const WebsiteScanLanding = lazyWithRetry(() => import("@/pages/WebsiteScanLanding"));
const WebsiteScanDetail = lazyWithRetry(() => import("@/pages/WebsiteScanDetail"));
const WebsiteDetail = lazyWithRetry(() => import("@/pages/WebsiteDetail"));
const AutomationAlerts = lazyWithRetry(() => import("@/pages/AutomationAlerts"));
const ReportsLanding = lazyWithRetry(() => import("@/pages/ReportsLanding"));
const AnalystFeed = lazyWithRetry(() => import("@/pages/AnalystFeed"));
const AnalystDetail = lazyWithRetry(() => import("@/pages/AnalystDetail"));
const AnalystMetrics = lazyWithRetry(() => import("@/pages/AnalystMetrics"));
const AnalystMetricDetail = lazyWithRetry(
  () => import("@/pages/AnalystMetricDetail"),
);
const AnalystOpen = lazyWithRetry(() => import("@/pages/AnalystOpen"));
const ReviewQueue = lazyWithRetry(() => import("@/pages/ReviewQueue"));
const DashboardReviewQueue = lazyWithRetry(() => import("@/pages/DashboardReviewQueue"));
const ReportView = lazyWithRetry(() => import("@/pages/ReportView"));
const EditionView = lazyWithRetry(() => import("@/pages/EditionView"));
const ReportDiffView = lazyWithRetry(() => import("@/pages/ReportDiffView"));
const ReportFullScreen = lazyWithRetry(() => import("@/pages/ReportFullScreen"));
const SegmentsLanding = lazyWithRetry(() => import("@/pages/SegmentsLanding"));
const SegmentBuilderPage = lazyWithRetry(() => import("@/pages/SegmentBuilderPage"));
const StorefrontPersonalizationPage = lazyWithRetry(
  () => import("@/pages/activation/StorefrontPersonalizationPage"),
);
const PersonalizationCampaignPage = lazyWithRetry(
  () => import("@/pages/activation/PersonalizationCampaignPage"),
);
const CampaignSettingsPage = lazyWithRetry(
  () => import("@/pages/activation/CampaignSettingsPage"),
);
const SurveysPage = lazyWithRetry(
  () => import("@/pages/activation/SurveysPage"),
);
// Onsite activation (plan 2026-10-07 on-site messages, A6); ships dark behind
// the `onsite_messages` account flag.
const OnsiteActivationPage = lazyWithRetry(
  () => import("@/pages/activation/OnsiteActivationPage"),
);
const OnsiteMessageDesignerPage = lazyWithRetry(
  () => import("@/pages/activation/OnsiteMessageDesignerPage"),
);
const JourneysPage = lazyWithRetry(
  () => import("@/pages/activation/JourneysPage"),
);
const JourneyBuilderPage = lazyWithRetry(
  () => import("@/pages/activation/JourneyBuilderPage"),
);
// Journey builder design lab (throwaway, plan 2026-10-07-journey-builder-ui).
const JourneyLabIndexPage = lazyWithRetry(
  () => import("@/pages/activation/journeyLab/JourneyLabIndexPage"),
);
const JourneyLabLivePage = lazyWithRetry(
  () => import("@/pages/activation/journeyLab/JourneyLabLivePage"),
);
const JourneyLabDesignedPage = lazyWithRetry(
  () => import("@/pages/activation/journeyLab/JourneyLabDesignedPage"),
);
const JourneyLabPage = lazyWithRetry(
  () => import("@/pages/activation/journeyLab/JourneyLabPage"),
);

function KeyedSegmentBuilderPage() {
  const { id } = useParams<{ id: string }>();
  return <SegmentBuilderPage key={id ?? "new"} />;
}

// Keyed like the segment builder: navigating between two campaigns (or from
// /:id to /new) must remount with a clean draft, not mutate a stale one.
function KeyedPersonalizationCampaignPage() {
  const { id } = useParams<{ id: string }>();
  return <PersonalizationCampaignPage key={id ?? "new"} />;
}

// Keyed for the same reason: a designer opened on message A must not keep A's
// draft when the route moves to message B (or to /new).
function KeyedOnsiteMessageDesignerPage() {
  const { messageId } = useParams<{ messageId: string }>();
  return <OnsiteMessageDesignerPage key={messageId ?? "new"} />;
}

// Keyed for the same reason: ReportView hoists iterate state (instruction,
// scope selection, chat session) that must not bleed from report A into
// report B when only the :publicId param changes. The remount also aborts
// any in-flight authoring stream via the panel's unmount cleanup.
// The dashboard page also opens a report (ADR-0496 D1, plan 2026-10-22 step
// A5): an account with Reports on and Dashboards off still reaches it.
const DASHBOARD_PAGE_SUBMENUS = ["dashboards", "reports"];

function KeyedReportView() {
  const { publicId } = useParams<{ publicId: string }>();
  // A report of the new kind is a dashboard row (ADR-0496 D1) and lives at
  // this address (plan 2026-10-26, D1): a `dash_` id opens the dashboard page
  // in report mode, any other id a legacy block report. The prefixes cannot
  // collide (`rpt_` vs `dash_`). Temporary: phase H removes the legacy branch.
  // The `dash_` branch admits either submenu like /insights/dashboards/:id,
  // because only the loaded row knows its kind; the page's kind gate and its
  // kind redirect take it from there (a dashboard goes back to Dashboards).
  if (publicId?.startsWith("dash_")) {
    return (
      <MenuGuard menu="insights" submenu={DASHBOARD_PAGE_SUBMENUS}>
        <MinDurationSuspense dark={false}>
          <Insights />
        </MinDurationSuspense>
      </MenuGuard>
    );
  }
  return (
    <MenuGuard menu="insights" submenu="reports">
      <MinDurationSuspense dark={false}>
        <ReportView key={publicId ?? "none"} />
      </MinDurationSuspense>
    </MenuGuard>
  );
}

// /activation/storefront-personalization was renamed to /activation/campaigns
// (2026-08-05). Bookmarks and older emails carry the old paths — redirect,
// keeping the querystring (?store=, ?tab=) that both pages read.
function LegacyPersonalizationRedirect() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const to = id ? `/activation/campaigns/${id}` : "/activation/campaigns";
  return <Navigate to={`${to}${location.search}`} replace />;
}

/**
 * What `/insights` resolves to.
 *
 * The Insights home page is built but not released — it sits behind the
 * `insights_home` account flag. While that is off, the section entry point is
 * Dashboards, matching the first leaf in SECTION_SUBMENU_LEAVES so a user who
 * clicks "Insights" and a user whose account lands on the section end up in the
 * same place.
 *
 * The inner MenuGuard still applies: an account can have the flag on and the
 * `home` submenu switched off, and then the home page must stay hidden.
 */
function InsightsSectionEntry() {
  const insightsHomeEnabled = useFeatureFlag("insights_home");

  if (!insightsHomeEnabled) {
    return <Navigate to="/insights/dashboards" replace />;
  }

  return (
    <MenuGuard menu="insights" submenu="home">
      <MinDurationSuspense dark={false}>
        <InsightsHome />
      </MinDurationSuspense>
    </MenuGuard>
  );
}

const SegmentDiscoveryPage = lazyWithRetry(() => import("@/pages/SegmentDiscoveryPage"));
const DiscoverySetupPage = lazyWithRetry(() => import("@/pages/DiscoverySetupPage"));
const ProfilesPage = lazyWithRetry(() => import("@/pages/ProfilesPage"));
const ProfileDetailPage = lazyWithRetry(() => import("@/pages/ProfileDetailPage"));

// Lazy load settings pages - these are not needed on initial load
const Users = lazyWithRetry(() => import("@/pages/settings/Users"));
const Workspaces = lazyWithRetry(() => import("@/pages/settings/Workspaces"));
const Agents = lazyWithRetry(() => import("@/pages/settings/Agents"));
const Profile = lazyWithRetry(() => import("@/pages/settings/Profile"));
const AccountPreferences = lazyWithRetry(() => import("@/pages/settings/AccountPreferences"));
const AnalystPerformance = lazyWithRetry(() => import("@/pages/settings/AnalystPerformance"));
const MarketingChannels = lazyWithRetry(() => import("@/pages/settings/MarketingChannels"));
const Targets = lazyWithRetry(() => import("@/pages/data-management/Targets"));
const AccountGlossary = lazyWithRetry(() => import("@/pages/settings/AccountGlossary"));
const FinancialStatements = lazyWithRetry(() => import("@/pages/data-management/FinancialStatements"));
const DataModels = lazyWithRetry(() => import("@/pages/data-management/DataModels"));
const DataManagement = lazyWithRetry(() => import("@/pages/settings/DataManagement"));
const DataManagementAuthorizations = lazyWithRetry(() => import("@/pages/data-management/DataManagementAuthorizations"));
const DataManagementDestinations = lazyWithRetry(() => import("@/pages/data-management/DataManagementDestinations"));
const DataManagementIntegrations = lazyWithRetry(() => import("@/pages/data-management/DataManagementIntegrations"));
const GitHubCallback = lazyWithRetry(() => import("@/pages/GitHubCallback"));
const AccountSettings = lazyWithRetry(() => import("@/pages/settings/AccountSettings"));
const Integrations = lazyWithRetry(() => import("@/pages/settings/Integrations"));
const SemanticLayer = lazyWithRetry(() => import("@/pages/settings/SemanticLayer"));
const McpSettings = lazyWithRetry(() => import("@/pages/settings/Mcp"));
const SiteSearchSettings = lazyWithRetry(() => import("@/pages/settings/SiteSearch"));

// Lazy load Facebook Insights (Legacy)
const FbInsights = lazyWithRetry(() => import("@/pages/FbInsights"));

// Lazy load superadmin pages
const SuperAdminClients = lazyWithRetry(() => import("@/pages/superadmin/SuperAdminClients"));
const SuperAdminPartners = lazyWithRetry(() => import("@/pages/superadmin/SuperAdminPartners"));
const PlatformAgentsSettings = lazyWithRetry(() => import("@/pages/superadmin/PlatformAgents"));
const PlatformGlossary = lazyWithRetry(() => import("@/pages/superadmin/PlatformGlossary"));
const PlatformPreferences = lazyWithRetry(() => import("@/pages/superadmin/PlatformPreferences"));
const PlatformSemanticLayer = lazyWithRetry(() => import("@/pages/superadmin/PlatformSemanticLayer"));
const FeedbackDashboard = lazyWithRetry(() => import("@/pages/superadmin/FeedbackDashboard"));
const ChatMonitor = lazyWithRetry(() => import("@/pages/superadmin/ChatMonitor"));
const PlatformPipelines = lazyWithRetry(() => import("@/pages/superadmin/PlatformPipelines"));
const PlatformSeedRuns = lazyWithRetry(() => import("@/pages/superadmin/PlatformSeedRuns"));
const PlatformGoldenQuestions = lazyWithRetry(() => import("@/pages/superadmin/PlatformGoldenQuestions"));
const AgentEvals = lazyWithRetry(() => import("@/pages/superadmin/AgentEvals"));
const ReportBlocksPreview = lazyWithRetry(() => import("@/pages/superadmin/ReportBlocksPreview"));
const Ops = lazyWithRetry(() => import("@/pages/superadmin/Ops"));
const OrchestrationBoard = lazyWithRetry(() => import("@/pages/superadmin/OrchestrationBoard"));

// Demo pages (temporary)
const ReportFunnelDemo = lazyWithRetry(() => import("@/pages/ReportFunnelDemo"));
const ReceiptDemo = lazyWithRetry(() => import("@/pages/ReceiptDemo"));
const DashboardLab = lazyWithRetry(() => import("@/pages/DashboardLab"));
/* Dev-only hero motion lab. Lazy so the variants never reach a production
   bundle — the route below is DEV-gated, but an eager import would ship the
   code regardless. */
const HeroMotionLab = lazyWithRetry(() => import("@/pages/HeroMotionLab"));
const CheckboxLab = lazyWithRetry(() => import("@/pages/CheckboxLab"));
const ToggleLab = lazyWithRetry(() => import("@/pages/ToggleLab"));
const OnsiteLab = lazyWithRetry(() => import("@/pages/OnsiteLab"));
const OnsiteTemplatesLab = lazyWithRetry(() => import("@/pages/OnsiteTemplatesLab"));

// Docs route subtree — lazy so the eager-frontmatter manifest only enters
// the bundle when a user visits /docs (not on every cold load).
const DocsLayout = lazyWithRetry(() =>
  import("@/components/docs/DocsLayout").then((m) => ({ default: m.DocsLayout }))
);
const DocsLandingPage = lazyWithRetry(() =>
  import("@/components/docs/DocsLandingPage").then((m) => ({ default: m.DocsLandingPage }))
);
const DocsCategoryPage = lazyWithRetry(() =>
  import("@/components/docs/DocsCategoryPage").then((m) => ({ default: m.DocsCategoryPage }))
);
const DocsRouteGuard = lazyWithRetry(() =>
  import("@/components/docs/DocsRouteGuard").then((m) => ({ default: m.DocsRouteGuard }))
);


/**
 * Lightweight OAuth callback page for Google.
 * Captures the authorization code from the URL, sends it to the
 * parent window (popup opener) via postMessage, then closes.
 */
function GoogleOAuthCallback() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const error = params.get("error");
    // `state` is only read by flows that need it (Drive context, ADR-0139);
    // the GA4/Ads listeners destructure code+error and ignore the rest.
    const state = params.get("state");

    if (window.opener) {
      window.opener.postMessage(
        { type: "google_oauth_callback", code, error, state },
        window.location.origin
      );
      window.close();
    }
  }, []);

  return (
    <Center h="100vh">
      <Stack align="center" gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Completing authentication...
        </Text>
      </Stack>
    </Center>
  );
}

/**
 * Lightweight OAuth callback page for Microsoft Advertising.
 * Captures the authorization code from the URL, sends it to the parent window
 * (popup opener) via postMessage, then closes.
 *
 * Unlike the Google listener, the Microsoft wizard REQUIRES `state`: the backend
 * verifies an account-bound signed state at exchange time, so it must survive the
 * round-trip back to the opener.
 */
function MicrosoftOAuthCallback() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    // Entra reports failures as error + error_description on the redirect.
    const error = params.get("error") || params.get("error_description");

    if (window.opener) {
      window.opener.postMessage(
        { type: "microsoft_oauth_callback", code, state, error },
        window.location.origin
      );
      window.close();
    }
  }, []);

  return (
    <Center h="100vh">
      <Stack align="center" gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Completing authentication...
        </Text>
      </Stack>
    </Center>
  );
}

/**
 * Lightweight OAuth callback page for Facebook.
 * Captures the authorization code from the URL, sends it to the
 * parent window (popup opener) via postMessage, then closes.
 */
function FacebookOAuthCallback() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const errorReason = params.get("error_reason");

    if (window.opener) {
      window.opener.postMessage(
        {
          type: "facebook_oauth_callback",
          code,
          state,
          error: error || errorReason,
        },
        window.location.origin
      );
      window.close();
    }
  }, []);

  return (
    <Center h="100vh">
      <Stack align="center" gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Completing authentication...
        </Text>
      </Stack>
    </Center>
  );
}

/**
 * Lightweight OAuth callback page for Shopify (popup landing). Relays the RAW
 * callback querystring to the opener (the wizard) — the backend verifies the
 * HMAC over that exact query — then closes.
 */
function ShopifyOAuthCallback() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("state");
    const error = params.get("error");
    const query = window.location.search.replace(/^\?/, "");
    const payload = { type: "shopify_oauth_callback", query, state, error };

    // Primary channel: postMessage to the opener. Works when the opener link
    // survives the round-trip.
    try {
      if (window.opener) {
        window.opener.postMessage(payload, window.location.origin);
      }
    } catch {
      /* opener may be cross-origin-severed; fall through to BroadcastChannel */
    }

    // Fallback channel: BroadcastChannel is same-origin and survives even when
    // Shopify's own login/consent page sets COOP (which severs window.opener in
    // the popup — the popup returns here with opener === null). Without this the
    // wizard never receives the callback and the flow hangs on this screen.
    try {
      const bc = new BroadcastChannel("shopify_oauth");
      bc.postMessage(payload);
      bc.close();
    } catch {
      /* BroadcastChannel unsupported — nothing more we can do here */
    }

    // Best-effort close; if the opener was severed the browser may refuse, so
    // the UI below also tells the user they can close the window themselves.
    try {
      window.close();
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <Center h="100vh">
      <Stack align="center" gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Completing authentication... You can close this window.
        </Text>
      </Stack>
    </Center>
  );
}


/**
 * Persistent authenticated shell — renders AppHeader once,
 * content swaps via <Outlet />. No header remount on navigation.
 */
function AuthenticatedShell() {
  const { pathname } = useLocation();
  return (
    <ProtectedRoute>
      <AssistantProvider>
        <SourceWizardProvider>
          <AuthorizationDrawerProvider>
            <div
              style={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                // Mantine AppShell normally sets this; we take over
                ["--app-shell-header-height" as string]: "64px",
              }}
            >
              <div style={{ height: 64, flexShrink: 0, borderBottom: "1px solid var(--mantine-color-gray-3)", boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)", position: "relative" }}>
                <AppHeader />
              </div>
              <div style={{ flex: 1, overflow: "auto", background: "#ffffff" }}>
                <AppErrorBoundary variant="route" resetKeys={[pathname]}>
                  <Outlet />
                </AppErrorBoundary>
              </div>
            </div>
            <OnboardingModal />
            {import.meta.env.DEV && <OnboardingDevToggle />}
          </AuthorizationDrawerProvider>
        </SourceWizardProvider>
      </AssistantProvider>
    </ProtectedRoute>
  );
}

/**
 * Every route inside an account, relative to `/a/:accountPublicId` (ADR-0302).
 *
 * Module-level so the same route objects serve two readers: the `<Routes>`
 * below, and the section-root lookup that decides where an account switch
 * lands (`matchRoutes` over `createRoutesFromElements(TENANT_ROUTES)`). Nested
 * routes cannot have absolute paths in react-router 7, hence relative `path`s.
 * Absolute paths inside elements (`<Navigate to="/…">`) are prefixed with the
 * tab's account by `@/routing/accountRouting`.
 */
export const TENANT_ROUTES = (
  <>
    {/* Index: resolve the account's landing section to a visible leaf. */}
    <Route index element={<LandingRedirect />} />
    <Route
      path="dashboard"
      element={
        <MenuGuard menu="dashboard">
          <MinDurationSuspense dark={false}>
            <Dashboard />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="setup/sources"
      element={
        <MinDurationSuspense dark={false}>
          <SourceSetupPage />
        </MinDurationSuspense>
      }
    />
    <Route
      path="notifications"
      element={
        <MinDurationSuspense dark={false}>
          <NotificationsPage />
        </MinDurationSuspense>
      }
    />
    <Route
      path="requests/:requestId"
      element={
        <MinDurationSuspense dark={false}>
          <AgentRequestPage />
        </MinDurationSuspense>
      }
    />

    {/* Analytics routes */}
    {/* Section entry point. Behind the `insights_home` account flag: with
        the flag off this is Dashboards, which is also what the landing
        resolver picks (SECTION_SUBMENU_LEAVES). Flipping the flag on in
        Superadmin → Clients swaps in the home page with no deploy. */}
    <Route
      path="insights"
      element={
        <MenuGuard menu="insights">
          <InsightsSectionEntry />
        </MenuGuard>
      }
    />
    <Route path="insights/chat/:conversationId?" element={<MenuGuard menu="insights" submenu="chat"><MinDurationSuspense dark={false}><ChatPage /></MinDurationSuspense></MenuGuard>} />
    <Route path="insights/dashboards" element={<MenuGuard menu="insights" submenu="dashboards"><MinDurationSuspense dark={false}><Insights /></MinDurationSuspense></MenuGuard>} />
    {/* Literal review-queue before the dynamic :dashboardId route, the
        same defence-in-depth as /reports/review-queue below. */}
    <Route path="insights/dashboards/review-queue" element={<MenuGuard menu="insights" submenu="dashboards"><MinDurationSuspense dark={false}><DashboardReviewQueue /></MinDurationSuspense></MenuGuard>} />
    <Route path="insights/dashboards/:dashboardId" element={<MenuGuard menu="insights" submenu={DASHBOARD_PAGE_SUBMENUS}><MinDurationSuspense dark={false}><Insights /></MinDurationSuspense></MenuGuard>} />
    <Route
      path="insights/dashboards/managed/fb"
      element={
        <MenuGuard menu="insights" submenu="dashboards">
          <MinDurationSuspense dark={false}>
            <FbInsights />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="insights/deep-dives"
      element={
        <MenuGuard menu="insights" submenu="deep-dives">
          <MinDurationSuspense dark={false}>
            <DeepDives />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="insights/deep-dives/new"
      element={
        <MenuGuard menu="insights" submenu="deep-dives">
          <MinDurationSuspense dark={false}>
            <DeepDiveNew />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="insights/deep-dives/:id"
      element={
        <MenuGuard menu="insights" submenu="deep-dives">
          <MinDurationSuspense dark={false}>
            <DeepDiveReport />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />

    {/* Automations */}
    <Route
      path="automations"
      element={
        <MenuGuard menu="automations" submenu="monitors">
          <MinDurationSuspense dark={false}>
            <AutomationsLanding />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="automations/new"
      element={
        <MenuGuard menu="automations" submenu="monitors">
          <MinDurationSuspense dark={false}>
            <AutomationCreate />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="automations/alerts"
      element={
        <MenuGuard menu="automations" submenu="alerts">
          <MinDurationSuspense dark={false}>
            <AutomationAlerts />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="automations/website-scan"
      element={
        <MenuGuard menu="automations" submenu="website-scan">
          <MinDurationSuspense dark={false}>
            <WebsiteScanLanding />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/*
      Declared BEFORE the scan-detail route below: react-router picks the
      more specific static segment, but keeping the literal path first
      keeps that obvious — "site" must never be read as a scan publicId.
    */}
    <Route
      path="automations/website-scan/site/:publicId"
      element={
        <MenuGuard menu="automations" submenu="website-scan">
          <MinDurationSuspense dark={false}>
            <WebsiteDetail />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="automations/website-scan/:publicId"
      element={
        <MenuGuard menu="automations" submenu="website-scan">
          <MinDurationSuspense dark={false}>
            <WebsiteScanDetail />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="automations/:id"
      element={
        <MenuGuard menu="automations" submenu="monitors">
          <MinDurationSuspense dark={false}>
            <AutomationDetail />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />

    {/* Analyst — gated as an Insights submenu (periodic-analyst §G).
        Routes live at /analyst/* because scheduled analyses notify with
        an action_url of /analyst/{public_id}.

        TWO gates, both required. ProtectedRoute admits the analyst's
        readers: account OWNER, ADMIN and MEMBER (plan 2026-10-24-whatsapp-
        daily-summary § D8). A MEMBER reads only the analyses of their own
        workspaces (the backend filters) and manages nothing: the pages hide
        every admin control through `canManageAnalyst`. VIEWER and SUPPORT
        stay out, as the backend's `require_analysis_reader` does. MenuGuard
        adds the per-account visibility gate. */}
    <Route
      path="analyst"
      element={
        <ProtectedRoute accountRoles={ANALYST_READER_ROLES} frame={InsightsSectionLayout}>
          <MenuGuard menu="insights" submenu="analyst">
            <MinDurationSuspense dark={false}>
              <AnalystFeed />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    {/* The two cross-run views of the statistics layer (plan
        2026-09-19-one-intelligence-layer § 6). They are the Metrics and
        Open TABS of the page above (plan 2026-09-20-metrics-and-open-are-
        tabs-on-the-analyst-page) and keep a route each, because a tab
        whose state is not in the URL cannot be linked or bookmarked.
        Hence the same `submenu="analyst"` gate as the feed: one menu
        entry, one visibility key, three tabs.

        Declared ABOVE `/analyst/:publicId` on purpose. React Router v6
        ranks by specificity rather than by declaration order, so a static
        segment already beats a dynamic one and the order is not what makes
        this work. Reading them in this order is how the next person sees
        the collision at all; `AnalystRouting.test.tsx` is what actually
        holds it. */}
    <Route
      path="analyst/metrics"
      element={
        <ProtectedRoute accountRoles={ANALYST_READER_ROLES} frame={InsightsSectionLayout}>
          <MenuGuard menu="insights" submenu="analyst">
            <MinDurationSuspense dark={false}>
              <AnalystMetrics />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route
      path="analyst/metrics/detail"
      element={
        <ProtectedRoute accountRoles={ANALYST_READER_ROLES} frame={InsightsSectionLayout}>
          <MenuGuard menu="insights" submenu="analyst">
            <MinDurationSuspense dark={false}>
              <AnalystMetricDetail />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route
      path="analyst/open"
      element={
        <ProtectedRoute accountRoles={ANALYST_READER_ROLES} frame={InsightsSectionLayout}>
          <MenuGuard menu="insights" submenu="analyst">
            <MinDurationSuspense dark={false}>
              <AnalystOpen />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route
      path="analyst/:publicId"
      element={
        <ProtectedRoute accountRoles={ANALYST_READER_ROLES} frame={InsightsSectionLayout}>
          <MenuGuard menu="insights" submenu="analyst">
            <MinDurationSuspense dark={false}>
              <AnalystDetail />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />

    {/* Reports — gated as an Insights submenu (W1, reports-ga-completion).
        The routes stay at /reports (URLs predate the menu entry). */}
    <Route
      path="reports"
      element={
        <MenuGuard menu="insights" submenu="reports">
          <MinDurationSuspense dark={false}>
            <ReportsLanding />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Literal /reports/review-queue MUST be declared BEFORE the
        dynamic /:publicId route — React Router does longest-match
        but we stay explicit for defense-in-depth. */}
    <Route
      path="reports/review-queue"
      element={
        <MenuGuard menu="insights" submenu="reports">
          <MinDurationSuspense dark={false}>
            <ReviewQueue />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* One edition of a report (plan 2026-10-23, C6): the report is a
        dashboard row, so the id is its `dash_` id. */}
    <Route
      path="reports/:dashboardId/editions/:editionPublicId"
      element={
        <MenuGuard menu="insights" submenu="reports">
          <MinDurationSuspense dark={false}>
            <EditionView />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Guarded inside: the id's prefix picks the page and its submenu. */}
    <Route path="reports/:publicId" element={<KeyedReportView />} />
    <Route
      path="reports/:publicId/fullscreen"
      element={
        <MenuGuard menu="insights" submenu="reports">
          <MinDurationSuspense dark={false}>
            <ReportFullScreen />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="reports/:publicId/diff"
      element={
        <MenuGuard menu="insights" submenu="reports">
          <MinDurationSuspense dark={false}>
            <ReportDiffView />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />

    {/* Settings routes */}
    <Route
      path="settings/account"
      element={
        <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={SettingsFrame}>
          <MinDurationSuspense dark={false}>
            <AccountSettings />
          </MinDurationSuspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="settings/integrations"
      element={
        <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={SettingsFrame}>
          <MenuGuard menu="settings" submenu="integrations">
            <MinDurationSuspense dark={false}>
              <Integrations />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route
      path="settings/users"
      element={
        <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={SettingsFrame}>
          <MinDurationSuspense dark={false}>
            <Users />
          </MinDurationSuspense>
        </ProtectedRoute>
      }
    />
    <Route
      path="settings/profile"
      element={
        <MinDurationSuspense dark={false}>
          <Profile />
        </MinDurationSuspense>
      }
    />
    <Route
      path="settings/workspaces"
      element={
        <MinDurationSuspense dark={false}>
          <Workspaces />
        </MinDurationSuspense>
      }
    />
    <Route path="settings/authorizations" element={<Navigate to="/data-management/authorizations" replace />} />
    <Route path="settings/data-warehouse" element={<Navigate to="/settings/account" replace />} />
    <Route path="settings/data-sources" element={<Navigate to="/data-management/sources" replace />} />
    <Route path="settings/data-management" element={<Navigate to="/data-management/sources" replace />} />
    <Route path="settings/agents" element={<Navigate to="/data-management/agents" replace />} />
    <Route path="settings/semantic-layer" element={<Navigate to="/data-management/semantic-layer" replace />} />
    <Route
      path="settings/preferences"
      element={
        <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={SettingsFrame}>
          <MenuGuard menu="settings" submenu="preferences">
            <MinDurationSuspense dark={false}>
              <AccountPreferences />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route
      path="settings/analyst-performance"
      element={
        <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={SettingsFrame}>
          <MenuGuard menu="settings" submenu="analyst-performance">
            <MinDurationSuspense dark={false}>
              <AnalystPerformance />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route
      path="settings/marketing-channels"
      element={
        <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={SettingsFrame}>
          <MenuGuard menu="settings" submenu="marketing-channels">
            <MinDurationSuspense dark={false}>
              <MarketingChannels />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route path="settings/targets" element={<Navigate to="/data-management/targets" replace />} />
    <Route
      path="settings/mcp"
      element={
        <ProtectedRoute>
          <MenuGuard menu="settings" submenu="mcp">
            <MinDurationSuspense dark={false}>
              <McpSettings />
            </MinDurationSuspense>
          </MenuGuard>
        </ProtectedRoute>
      }
    />
    <Route path="settings/glossary" element={<Navigate to="/data-management/glossary" replace />} />
    {/* Site search per pixel site (plan 2026-10-28-site-search, step 5.4),
        dark behind the account flag. The URL shape is a contract: an MCP tool
        builds links to /settings/site-search/:siteId?section=…. */}
    <Route
      path="settings/site-search"
      element={
        <ProtectedRoute>
          <FeatureFlagGuard flag="site_search">
            <MinDurationSuspense dark={false}>
              <SiteSearchSettings />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </ProtectedRoute>
      }
    />
    <Route
      path="settings/site-search/:siteId"
      element={
        <ProtectedRoute>
          <FeatureFlagGuard flag="site_search">
            <MinDurationSuspense dark={false}>
              <SiteSearchSettings />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </ProtectedRoute>
      }
    />

    {/* Data Management routes */}
    <Route path="data-management" element={<MenuGuard menu="data-management"><Navigate to="/data-management/sources" replace /></MenuGuard>} />
    <Route
      path="data-management/sources"
      element={
        <MenuGuard menu="data-management" submenu="sources">
          <MinDurationSuspense dark={false}>
            <DataManagement />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/sources/:id"
      element={
        <MenuGuard menu="data-management" submenu="sources">
          <MinDurationSuspense dark={false}>
            <DataManagement />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/flows"
      element={
        <MenuGuard menu="data-management" submenu="flows">
          <MinDurationSuspense dark={false}>
            <DataManagement />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/flows/:id"
      element={
        <MenuGuard menu="data-management" submenu="flows">
          <MinDurationSuspense dark={false}>
            <DataManagement />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Lineage is a TAB on the sources page, not a menu entry of its own —
        hence the sources submenu guard (plan 2026-07-28-lineage-tab). */}
    <Route
      path="data-management/lineage"
      element={
        <MenuGuard menu="data-management" submenu="sources">
          <MinDurationSuspense dark={false}>
            <DataManagement />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/integrations"
      element={
        <MenuGuard menu="data-management" submenu="integrations">
          <MinDurationSuspense dark={false}>
            <DataManagementIntegrations />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/authorizations"
      element={
        <MenuGuard menu="data-management" submenu="authorizations">
          <MinDurationSuspense dark={false}>
            <DataManagementAuthorizations />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/authorizations/:id"
      element={
        <MenuGuard menu="data-management" submenu="authorizations">
          <MinDurationSuspense dark={false}>
            <DataManagementAuthorizations />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/destinations"
      element={
        <MenuGuard menu="data-management" submenu="destinations">
          <MinDurationSuspense dark={false}>
            <DataManagementDestinations />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Legacy route — keep for one release per master plan Phase 7
        (docs/plans-pending/2026-05-14-unified-destinations-model.md).
        Bookmarks land on the new unified page; rip out at Phase 10. */}
    <Route
      path="data-management/kv-destinations"
      element={<Navigate to="/data-management/destinations" replace />}
    />
    <Route
      path="data-management/semantic-layer"
      element={
        <MenuGuard menu="data-management" submenu="semantic-layer">
          <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={DataManagementFrame}>
            <MinDurationSuspense dark={false}>
              <SemanticLayer />
            </MinDurationSuspense>
          </ProtectedRoute>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/targets"
      element={
        <MenuGuard menu="data-management" submenu="targets">
          <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={DataManagementFrame}>
            <MinDurationSuspense dark={false}>
              <Targets />
            </MinDurationSuspense>
          </ProtectedRoute>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/financial-statements"
      element={
        <MenuGuard menu="data-management" submenu="financial-statements">
          <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={DataManagementFrame}>
            <MinDurationSuspense dark={false}>
              <FinancialStatements />
            </MinDurationSuspense>
          </ProtectedRoute>
        </MenuGuard>
      }
    />
    {/* Customer models (plan 2026-10-21-customer-data-modeling-platform, P2.6).
        The flag gate is the submenu's `featureFlag` (MENU_REGISTRY); the API
        answers 404 for an account without it. */}
    <Route
      path="data-management/models"
      element={
        <MenuGuard menu="data-management" submenu="models">
          <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={DataManagementFrame}>
            <MinDurationSuspense dark={false}>
              <DataModels />
            </MinDurationSuspense>
          </ProtectedRoute>
        </MenuGuard>
      }
    />
    <Route
      path="data-management/glossary"
      element={
        <MenuGuard menu="data-management" submenu="glossary">
          <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={DataManagementFrame}>
            <MinDurationSuspense dark={false}>
              <AccountGlossary />
            </MinDurationSuspense>
          </ProtectedRoute>
        </MenuGuard>
      }
    />

    <Route
      path="data-management/agents"
      element={
        <MenuGuard menu="data-management" submenu="agents">
          <ProtectedRoute accountRoles={["OWNER", "ADMIN"]} frame={DataManagementFrame}>
            <MinDurationSuspense dark={false}>
              <Agents />
            </MinDurationSuspense>
          </ProtectedRoute>
        </MenuGuard>
      }
    />

    {/* Activation — Segments */}
    <Route
      path="activation/segments"
      element={
        <MenuGuard menu="activation" submenu="segments">
          <MinDurationSuspense dark={false}>
            <SegmentsLanding />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="activation/segments/discovery"
      element={
        <MenuGuard menu="activation" submenu="segments">
          <MinDurationSuspense dark={false}>
            <SegmentDiscoveryPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="activation/segments/discovery/setup"
      element={
        <MenuGuard menu="activation" submenu="segments">
          <MinDurationSuspense dark={false}>
            <DiscoverySetupPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="activation/segments/new"
      element={
        <MenuGuard menu="activation" submenu="segments">
          <MinDurationSuspense dark={false}>
            <KeyedSegmentBuilderPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="activation/segments/:id"
      element={
        <MenuGuard menu="activation" submenu="segments">
          <MinDurationSuspense dark={false}>
            <KeyedSegmentBuilderPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />

    {/* Activation — Profiles */}
    <Route
      path="activation/profiles"
      element={
        <MenuGuard menu="activation" submenu="segments">
          <MinDurationSuspense dark={false}>
            <ProfilesPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="activation/profiles/:profileId"
      element={
        <MenuGuard menu="activation" submenu="segments">
          <MinDurationSuspense dark={false}>
            <ProfileDetailPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="activation/campaigns"
      element={
        <MenuGuard menu="activation" submenu="campaigns">
          <MinDurationSuspense dark={false}>
            <StorefrontPersonalizationPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Static segment, so it outranks /:id below regardless of order
        (react-router scores static 10 vs dynamic 3) — the same shape
        /new already relies on. */}
    <Route
      path="activation/campaigns/settings"
      element={
        <MenuGuard menu="activation" submenu="campaigns">
          <MinDurationSuspense dark={false}>
            <CampaignSettingsPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Onsite activation ships dark behind the account flag (plan
        2026-10-07-on-site-messages-via-the-pixel, A6): the campaigns submenu
        gate plus the flag guard, which redirects while the flag is off. The
        static `onsite` segment outranks /:id below. */}
    <Route
      path="activation/campaigns/onsite"
      element={
        <MenuGuard menu="activation" submenu="campaigns">
          <FeatureFlagGuard flag="onsite_messages">
            <MinDurationSuspense dark={false}>
              <OnsiteActivationPage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    <Route
      path="activation/campaigns/onsite/messages/:messageId"
      element={
        <MenuGuard menu="activation" submenu="campaigns">
          <FeatureFlagGuard flag="onsite_messages">
            <MinDurationSuspense dark={false}>
              <KeyedOnsiteMessageDesignerPage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    <Route
      path="activation/campaigns/new"
      element={
        <MenuGuard menu="activation" submenu="campaigns">
          <MinDurationSuspense dark={false}>
            <KeyedPersonalizationCampaignPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    <Route
      path="activation/campaigns/:id"
      element={
        <MenuGuard menu="activation" submenu="campaigns">
          <MinDurationSuspense dark={false}>
            <KeyedPersonalizationCampaignPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Journeys ship dark behind the account flag (plan 2026-10-02-journeys-v1):
        the submenu gate as every Activation page, plus the flag guard, which
        redirects like a hidden menu while the flag is off. */}
    <Route
      path="activation/journeys"
      element={
        <MenuGuard menu="activation" submenu="journeys">
          <FeatureFlagGuard flag="journeys">
            <MinDurationSuspense dark={false}>
              <JourneysPage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    {/* Journey builder design lab (throwaway, plan 2026-10-07-journey-builder-ui):
        the static `lab` segment outranks `:id`. Removed with the real builder. */}
    <Route
      path="activation/journeys/lab"
      element={
        <MenuGuard menu="activation" submenu="journeys">
          <FeatureFlagGuard flag="journeys">
            <MinDurationSuspense dark={false}>
              <JourneyLabIndexPage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    <Route
      path="activation/journeys/lab/live/:id"
      element={
        <MenuGuard menu="activation" submenu="journeys">
          <FeatureFlagGuard flag="journeys">
            <MinDurationSuspense dark={false}>
              <JourneyLabLivePage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    <Route
      path="activation/journeys/lab/designed/:id"
      element={
        <MenuGuard menu="activation" submenu="journeys">
          <FeatureFlagGuard flag="journeys">
            <MinDurationSuspense dark={false}>
              <JourneyLabDesignedPage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    <Route
      path="activation/journeys/lab/:option"
      element={
        <MenuGuard menu="activation" submenu="journeys">
          <FeatureFlagGuard flag="journeys">
            <MinDurationSuspense dark={false}>
              <JourneyLabPage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    <Route
      path="activation/journeys/:id"
      element={
        <MenuGuard menu="activation" submenu="journeys">
          <FeatureFlagGuard flag="journeys">
            <MinDurationSuspense dark={false}>
              <JourneyBuilderPage />
            </MinDurationSuspense>
          </FeatureFlagGuard>
        </MenuGuard>
      }
    />
    <Route
      path="activation/surveys"
      element={
        <MenuGuard menu="activation" submenu="surveys">
          <MinDurationSuspense dark={false}>
            <SurveysPage />
          </MinDurationSuspense>
        </MenuGuard>
      }
    />
    {/* Pre-rename paths (2026-08-05) — the :id form also catches /new. */}
    <Route
      path="activation/storefront-personalization"
      element={<LegacyPersonalizationRedirect />}
    />
    <Route
      path="activation/storefront-personalization/campaigns/:id"
      element={<LegacyPersonalizationRedirect />}
    />


    {/* Unknown path inside an account: its landing page. */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </>
);

registerTenantRoutes(createRoutesFromElements(TENANT_ROUTES));

function AppRoutes() {
  const { isAuthenticated, activeAccount } = useAuth();
  const { pathname: currentPath } = useLocation();

  /* Dev-only bake-off rig for the dashboard hero entrance. Deliberately placed
     BEFORE the auth gate: it is a static comparison page with no data
     dependencies, and a rig you can only open with a running BFF and a valid
     session is a rig nobody opens. Lazy-loaded, and stripped from production
     builds by the DEV guard.

     Three of them now: the hero entrance (/motion-lab), the checkbox mark
     (/checkbox-lab) and the toggle (/toggle-lab). Same placement, same
     reasoning, and each one is the only place the corresponding theme default
     can be seen without overrides on top of it.
     See docs/plans-pending/2026-07-31-dashboard-hero-motion-lab.md */
  if (import.meta.env.DEV && currentPath === "/checkbox-lab") {
    return (
      <Routes>
        <Route
          path="/checkbox-lab"
          element={
            <MinDurationSuspense dark={false}>
              <CheckboxLab />
            </MinDurationSuspense>
          }
        />
      </Routes>
    );
  }

  /* Design rig for the onsite message campaign editor
     (plan 2026-10-08-onsite-message-editor-redesign). */
  if (import.meta.env.DEV && currentPath === "/onsite-lab") {
    return (
      <Routes>
        <Route
          path="/onsite-lab"
          element={
            <MinDurationSuspense dark={false}>
              <OnsiteLab />
            </MinDurationSuspense>
          }
        />
      </Routes>
    );
  }

  /* Design rig for onsite templates, blocks and display settings
     (plan 2026-10-29-onsite-templates-blocks-and-targeting, step 0.1). */
  if (import.meta.env.DEV && currentPath === "/onsite-templates-lab") {
    return (
      <Routes>
        <Route
          path="/onsite-templates-lab"
          element={
            <MinDurationSuspense dark={false}>
              <OnsiteTemplatesLab />
            </MinDurationSuspense>
          }
        />
      </Routes>
    );
  }

  /* Same rig for the toggle. */
  if (import.meta.env.DEV && currentPath === "/toggle-lab") {
    return (
      <Routes>
        <Route
          path="/toggle-lab"
          element={
            <MinDurationSuspense dark={false}>
              <ToggleLab />
            </MinDurationSuspense>
          }
        />
      </Routes>
    );
  }

  if (import.meta.env.DEV && currentPath === "/motion-lab") {
    return (
      <Routes>
        <Route
          path="/motion-lab"
          element={
            <MinDurationSuspense dark={false}>
              <HeroMotionLab />
            </MinDurationSuspense>
          }
        />
      </Routes>
    );
  }

  // The loader gates on the FIRST auth pass only; a later sign-in or sign-out
  // leaves every route mounted (AuthBootstrapGate, D6).
  return (
    <AuthBootstrapGate>
    <Routes>
      {/* Public / non-shell routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route path="/logout" element={<MinDurationSuspense dark={false}><Logout /></MinDurationSuspense>} />
      <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftOAuthCallback />} />
      <Route path="/auth/facebook/callback" element={<FacebookOAuthCallback />} />
      <Route path="/auth/shopify/callback" element={<ShopifyOAuthCallback />} />
      <Route path="/auth/github/callback" element={<MinDurationSuspense dark={false}><GitHubCallback /></MinDurationSuspense>} />
      <Route path="/auth/email-callback" element={<MinDurationSuspense dark={false}><MagicLinkCallback /></MinDurationSuspense>} />
      <Route path="/accept-invite" element={<MinDurationSuspense dark={false}><AcceptInvite /></MinDurationSuspense>} />
      {/* Mock-only design preview, no session needed (plan
          2026-10-16-a-receipt-visualization, step 1). */}
      <Route
        path="/demo/receipt"
        element={
          <MinDurationSuspense dark={false}>
            <ReceiptDemo />
          </MinDurationSuspense>
        }
      />
      {/* Dev-only design showcase; guarded out of production so its
          hardcoded verity wordmark can't leak onto a partner domain. */}
      {import.meta.env.DEV && (
        <Route path="/loaders" element={<VerityLoaderShowcase />} />
      )}

      {/* Authenticated shell — persistent AppHeader */}
      {/* Keyed on the active account: a switch remounts the shell and its
          account-scoped providers, as the global loading flag used to do by
          unmounting everything. Switching back from the mismatch notice keeps
          the same active account in a stale tab, so the page under the notice
          (and what was typed into it) survives. */}
      <Route element={<AuthenticatedShell key={activeAccount?.id ?? "none"} />}>
        {/* Index: resolve the account's landing section to a visible leaf. */}
        <Route path="/" element={<LandingRedirect />} />
        {/* Dev-only scratch page. Guarded out of production so its
            hardcoded Verity logo/label can't leak onto a partner domain
            (white-label plan stap 2.2). */}
        {import.meta.env.DEV && (
          <Route path="/testcode" element={<TestPage />} />
        )}

        {/* Customer docs (knowledge base) — universal entry point, not module-gated.
            Per-article gating runs inside DocsRouteGuard; the article element
            is keyed on activeAccount?.id so an account switch mid-load forces
            a clean remount instead of flashing a stale article body. */}
        <Route
          path="/docs"
          element={
            <MinDurationSuspense dark={false}>
              <DocsLayout />
            </MinDurationSuspense>
          }
        >
          <Route index element={<DocsLandingPage />} />
          <Route path=":category" element={<DocsCategoryPage />} />
          <Route
            path=":category/*"
            element={<DocsRouteGuard key={activeAccount?.id} />}
          />
        </Route>

        {/* Superadmin routes */}
        <Route
          path="/settings/clients"
          element={
            <ProtectedRoute requireSuperadminOrPartnerAdmin>
              <MinDurationSuspense dark={false}>
                <SuperAdminClients />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/partners"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <SuperAdminPartners />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/agents"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <PlatformAgentsSettings />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/glossary"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <PlatformGlossary />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/preferences"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <PlatformPreferences />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/semantic-layer"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <PlatformSemanticLayer />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/feedback"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <FeedbackDashboard />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/chat-monitor"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <ChatMonitor />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/pipelines"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <PlatformPipelines />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/seeds"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <PlatformSeedRuns />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/golden-questions"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <PlatformGoldenQuestions />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/evals"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <AgentEvals />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/platform-settings/report-blocks"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <ReportBlocksPreview />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/ops"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <Ops />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-settings/orchestration"
          element={
            <ProtectedRoute requireSuperadmin>
              <MinDurationSuspense dark={false}>
                <OrchestrationBoard />
              </MinDurationSuspense>
            </ProtectedRoute>
          }
        />

        {/* Demo pages (temporary) */}
        <Route
          path="/demo/report-funnel"
          element={
            <MinDurationSuspense dark={false}>
              <ReportFunnelDemo />
            </MinDurationSuspense>
          }
        />

        {/* Permanent design lab (plan 2026-10-16-dashboard-design-lab-test-page):
            the real dashboard tiles on generated example data. Behind login,
            unlinked, every environment (Arjen, 2026-10-16). */}
        <Route
          path="/demo/dashboard-lab"
          element={
            <MinDurationSuspense dark={false}>
              <DashboardLab />
            </MinDurationSuspense>
          }
        />

        {/* Every tenant page lives under /a/<accountPublicId> (ADR-0302). */}
        <Route path={TENANT_ROUTE_BASE} element={<TenantScope />}>
          {TENANT_ROUTES}
        </Route>

        {/* Any other path: an old unprefixed tenant URL (bookmark, mailed
            link) moves into the session's account, search and hash kept. */}
        <Route path="*" element={<LegacyTenantRedirect />} />
      </Route>
    </Routes>
    </AuthBootstrapGate>
  );
}


/**
 * The whole app, rendered as the element of the router's one splat route
 * (`routing/appRouter.ts`, created in `main.tsx`).
 */
function App() {
  return (
      <AuthProvider>
        <DashboardDefaultsProvider>
        <NotificationProvider>
          <AccountScopedWatermarks>
          <DwhProvisioningProvider>
            <ReportBuildProvider>
            <GeneratingMessagesProvider>
              <PreferencesProvider>
                <WorkspaceProvider>
                  <DiscoveryJobProvider>
                    <OnboardingProvider>
                      <AppRoutes />
                    </OnboardingProvider>
                  </DiscoveryJobProvider>
                </WorkspaceProvider>
              </PreferencesProvider>
            </GeneratingMessagesProvider>
            </ReportBuildProvider>
          </DwhProvisioningProvider>
          </AccountScopedWatermarks>
        </NotificationProvider>
        </DashboardDefaultsProvider>
      </AuthProvider>
  );
}

export default App;
